// Kapanış kartı — 3 ton: rahat / acil / sakin.
// Tone artık Home.tsx'te kural-bazlı hesaplanır (pendingPushToday + ratio'dan).
// Bu component görsel temsil + kapatma davranışı.

import { useState } from "react";

export type KapanisTone = "rahat" | "acil" | "sakin";

interface Props {
  tone?: KapanisTone;
  /** True → "değerlendirildi" varyant metni (sadece rahat tonunda devreye girer) */
  evaluatedAny?: boolean;
  /** Bugünkü push sinyal sayısı — meta metnine yansır */
  pendingPushToday?: number;
  /** Portföy / hedef oranı (0..1+); null ise meta'da pay göstergesi gizlenir */
  ratio?: number | null;
}

function fmtRatioMeta(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return "";
  }
  const pct = Math.round(ratio * 100);
  return `${pct}% dolu`;
}

interface ToneCfg {
  title: string;
  body: string;
  meta: string;
  evaluatedBody?: string;
  evaluatedMeta?: string;
  cardClass: string;
  titleColor: string;
  dotColor: string;
}

const CONFIG: Record<KapanisTone, ToneCfg> = {
  rahat: {
    title: "Kapanış · rahat",
    body: "Bugün hedef sizden 3 gün önde. Masaya kadar bekleyebilir.",
    meta: "+3 gün önde",
    evaluatedBody: "TLY sinyali değerlendirildi. Bugün hedef hâlâ 3 gün önde.",
    evaluatedMeta: "+3 gün önde · 1/1 sinyal",
    cardClass: "bg-calm-500/10 border-calm-500/40",
    titleColor: "text-calm-400",
    dotColor: "bg-calm-400",
  },
  acil: {
    title: "Kapanış · acil",
    body: "Şimdi otur ve karar ver. 2 sinyal bekliyor, hedef geride.",
    meta: "−2 gün geride · 2 sinyal",
    cardClass: "bg-warn-500/10 border-warn-500/50",
    titleColor: "text-warn-400",
    dotColor: "bg-warn-400",
  },
  sakin: {
    title: "Kapanış · sakin",
    body: "Bugün es geçebilirsin. Bir şey değişmedi.",
    meta: "±0 gün",
    cardClass: "bg-ink-700/40 border-ink-600",
    titleColor: "text-ink-300",
    dotColor: "bg-ink-400",
  },
};

export default function KapanisKart({
  tone = "rahat",
  evaluatedAny = false,
  pendingPushToday,
  ratio,
}: Props) {
  const [closed, setClosed] = useState(false);
  const [closedAt, setClosedAt] = useState<string | null>(null);

  const cfg = CONFIG[tone];
  const showEvaluated = tone === "rahat" && evaluatedAny && !closed;

  // Canlı meta: "N sinyal · X% dolu" — pendingPushToday undefined ise default cfg.meta.
  const liveMeta =
    pendingPushToday !== undefined
      ? `${pendingPushToday} sinyal${
          fmtRatioMeta(ratio) ? ` · ${fmtRatioMeta(ratio)}` : ""
        }`
      : null;

  // Canlı body — sinyal sayısı + oran kombinasyonu metnini açıklayıcı yap.
  // (cfg.body sabit metinler değiştirilmeden kalır — fallback olarak.)
  const liveBody = (() => {
    if (pendingPushToday === undefined) return null;
    if (tone === "acil") {
      return pendingPushToday > 0
        ? `Şimdi otur ve karar ver. ${pendingPushToday} sinyal bekliyor.`
        : "Hedef geride — portföye dönüp planı yenile.";
    }
    if (tone === "rahat") {
      return "Bugün hedefin önündesiniz. Masaya kadar bekleyebilir.";
    }
    // sakin — ratio null'sa veri yok demektir, açıkça söyle
    if (ratio === null || ratio === undefined) {
      return "Veri yok — Ayarlar'dan token gir.";
    }
    return "Bugün es geçebilirsin. Önemli bir değişiklik yok.";
  })();

  const body = closed
    ? "✓ Gün kapatıldı. Yarın 08:00'de tekrar açılır."
    : showEvaluated && cfg.evaluatedBody
      ? cfg.evaluatedBody
      : liveBody ?? cfg.body;
  const meta = closed
    ? `kapatıldı · ${closedAt}`
    : showEvaluated && cfg.evaluatedMeta
      ? cfg.evaluatedMeta
      : liveMeta ?? cfg.meta;

  function markClosed() {
    setClosed(true);
    setClosedAt(
      new Date().toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  }

  return (
    <section className="px-4 pb-4" aria-label="Günün kapanışı">
      <article
        className={`rounded-2xl border closing-card card-shadow p-4 ${cfg.cardClass}`}
      >
        <header className="flex items-center gap-2">
          <span
            className={`w-2.5 h-2.5 rounded-full ${cfg.dotColor}`}
            aria-hidden="true"
          />
          <h3
            className={`text-[11px] uppercase tracking-wider font-semibold ${cfg.titleColor}`}
          >
            {cfg.title}
          </h3>
          <span className="ml-auto text-[11px] text-ink-400 fon-dili">
            {meta}
          </span>
        </header>
        <p className="hedef-dili text-ink-50 text-base leading-snug mt-2">
          {body}
        </p>
        {!closed && (
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={markClosed}
              className="grow rounded-lg bg-accent-500 hover:bg-accent-400 text-ink-900 font-medium text-sm py-2.5 transition"
            >
              Günü kapat
            </button>
            <button
              type="button"
              onClick={() => alert("Detay sayfası henüz açılmadı.")}
              className="rounded-lg bg-ink-700 hover:bg-ink-600 text-ink-100 font-medium text-sm py-2.5 px-4 transition"
            >
              Detay
            </button>
          </div>
        )}
      </article>
    </section>
  );
}
